import { EventActions } from '../actions'
import { assert } from '../assert'
import type { ButtonProps } from '../components/button/button'
import type { SelectOption } from '../components/forms/select/select'
import type { ProductsSidebarProps } from '../components/products-sidebar/products-sidebar'
import type {
    ProgressBarItemProps,
    ProgressBarProps,
} from '../components/progress-bar/progress-bar'
import { handleError } from '../handle-error'
import type {
    CheckoutProps,
    SelectedSeatItem,
    SelectedSeatProps,
} from '../modules/checkout/checkout'
import type { EventSelectionSectionProps } from '../modules/checkout/event-selection-section'
import { Storage } from '../storage'
import { CheckoutStep } from '../types'
import type { EventData, EventState, EventView, PluginSession } from '../types'
import { handleGtmPay } from '../utils/handle-gtm-pay'
import { getCardsSection } from './cards-section'
import { getContactSection } from './contact-section'
import { getFloorplanSection } from './floorplan-section'
import { getPaymentSection } from './payment-section'
import { getProductFilters } from './product-filters-section'
import { getProductsList } from './products-list-section'
import { getTicketsSection } from './tickets-section'

const anyTicketsAdded = (state: EventState) => state.calc.tickets.length > 0

const isNavigationAllowed = (view: EventView) => {
    const { state } = view
    const { isAddProductsFlow, session, selectedTicket } = state

    if (isAddProductsFlow) {
        return (
            session &&
            session?.eventUuid === selectedTicket.eventUuid &&
            session?.rowNumber === selectedTicket.row &&
            session?.seatNumber === selectedTicket.seat
        )
    }

    return anyTicketsAdded(state)
}

const getProgressBarProps = (view: EventView): ProgressBarProps => {
    const { state } = view
    const { lang, activeCheckoutStep } = state

    const navigationAllowed = isNavigationAllowed(view)

    const items: ProgressBarItemProps[] = [
        {
            text: lang.progressBar_tickets,
            completed: activeCheckoutStep !== CheckoutStep.TICKETS,
            onClick: () => {
                state.activeCheckoutStep = CheckoutStep.TICKETS
                view.update()
            },
        },
    ]

    const productsStep: ProgressBarItemProps = {
        text: lang.progressBar_products,
        completed: activeCheckoutStep === CheckoutStep.SUMMARY,
        disabled: !navigationAllowed,
    }

    const summaryStep: ProgressBarItemProps = {
        text: lang.progressBar_summary,
        disabled: !navigationAllowed,
    }

    if (navigationAllowed) {
        productsStep.onClick = () => {
            state.activeCheckoutStep = CheckoutStep.PRODUCTS
            view.update()
        }

        summaryStep.onClick = () => {
            state.activeCheckoutStep = CheckoutStep.SUMMARY
            view.update()
        }
    }

    items.push(productsStep)
    items.push(summaryStep)

    return {
        items,
        activeIndex: Object.values(CheckoutStep).indexOf(
            activeCheckoutStep ?? CheckoutStep.TICKETS,
        ),
    }
}

const addNavigationButtons = (checkout: CheckoutProps, view: EventView) => {
    const { state, update } = view
    const { lang, eventsData, selectedTicket, activeCheckoutStep } = state

    if (activeCheckoutStep === CheckoutStep.TICKETS) {
        const chooseProductsButton: ButtonProps = {
            text: lang.chooseProducts,
        }

        if (state.isAddProductsFlow) {
            const event = eventsData?.find((e) => e.eventUuid === selectedTicket.eventUuid)
            chooseProductsButton.isDisabled =
                !event || (Boolean(event.maxSeat) && !(selectedTicket?.seat && selectedTicket?.row))

            chooseProductsButton.onClick = async () => {
                state.isLoading = true
                update()

                setTimeout(() => {
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    })
                }, 100)

                try {
                    const session: PluginSession = {
                        eventUuid: selectedTicket.eventUuid ?? '',
                        rowNumber: selectedTicket.row,
                        seatNumber: selectedTicket.seat,
                    }

                    await EventActions.updateSession(session)

                    state.session = session
                    state.activeCheckoutStep = CheckoutStep.PRODUCTS
                    state.eventUuid = session.eventUuid

                    await EventActions.loadEvent(view)
                } catch (error) {
                    handleError(view, error as Error)
                } finally {
                    state.isLoading = false
                    update()
                }
            }
        } else {
            chooseProductsButton.isDisabled = !anyTicketsAdded(state)
            chooseProductsButton.onClick = () => {
                state.activeCheckoutStep = CheckoutStep.PRODUCTS
                view.update()

                setTimeout(() => {
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    })
                }, 100)
            }
        }

        checkout.buttons.push(chooseProductsButton)
    } else if (activeCheckoutStep === CheckoutStep.PRODUCTS) {
        checkout.buttons.push({
            text: lang.back,
            onClick: () => {
                state.activeCheckoutStep = CheckoutStep.TICKETS
                view.update()
                setTimeout(() => {
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    })
                }, 100)
            },
            class: 'btn--secondary',
        })
        checkout.buttons.push({
            text: lang.completePurchase,
            onClick: () => {
                state.activeCheckoutStep = CheckoutStep.SUMMARY
                view.update()
                setTimeout(() => {
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    })
                }, 100)
            },
        })
    } else if (activeCheckoutStep === CheckoutStep.SUMMARY) {
        checkout.buttons.push({
            text: lang.back,
            onClick: () => {
                if (checkout.error) {
                    state.activeCheckoutStep = CheckoutStep.TICKETS
                } else {
                    state.activeCheckoutStep = CheckoutStep.PRODUCTS
                }
                view.update()
                setTimeout(() => {
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    })
                }, 100)
            },
            class: 'btn--secondary',
        })
    }
}

const getEventSelectionProps = (view: EventView): EventSelectionSectionProps => {
    const { state, update } = view
    const { lang, eventsData, selectedTicket } = state

    assert(eventsData)

    const productions = new Map<string, SelectOption>()
    const events: EventData[] = []
    let selectedEvent: EventData | undefined

    for (const event of eventsData) {
        if (!productions.has(event.productionUuid)) {
            productions.set(event.productionUuid, {
                value: event.productionUuid,
                name: event.productionName,
            })
        }

        if (event.productionUuid === selectedTicket.productionUuid) {
            events.push(event)
        }

        if (event.eventUuid === selectedTicket.eventUuid) {
            selectedEvent = event
        }
    }

    const eventOptions = events.length
        ? events.map((event): SelectOption => {
              const venue = event.venueName ? `${event.venueName}, ` : ''
              const room = event.roomName ? `${event.roomName}, ` : ''

              return {
                  value: event.eventUuid,
                  name: venue + room + event.eventStartTime,
              }
          })
        : [{ value: '', name: lang.eventSelection_chooseEvent }]

    const productionOptions = [...productions.values()].sort((p1, p2) =>
        p1.name.localeCompare(p2.name),
    )

    const eventSelection: EventSelectionSectionProps = {
        chooseEventText: lang.eventSelection_chooseEventTitle,
        production: {
            class: 'select--hide-label',
            default: lang.eventSelection_chooseProduction,
            options: productionOptions,
            value: selectedTicket.productionUuid ?? '',
            onChange: (value: string) => {
                state.selectedTicket.productionUuid = value
                delete state.selectedTicket.eventUuid
                delete state.selectedTicket.row
                delete state.selectedTicket.seat
                update()
            },
        },
        event: {
            class: 'select--hide-label',
            default: lang.eventSelection_chooseEvent,
            options: eventOptions,
            value: selectedTicket.eventUuid ?? '',
            onChange: (value: string) => {
                state.selectedTicket.eventUuid = value
                delete state.selectedTicket.row
                delete state.selectedTicket.seat
                update()
            },
            isDisabled: !events.length,
        },
    }

    if (selectedEvent && !selectedEvent.maxRow) {
        eventSelection.seatInfo = lang.eventSelection_noNumberedSeats
    } else {
        eventSelection.seatSelection = {
            chooseSeatText: lang.eventSelection_chooseSeatTitle,
            row: {
                class: 'select--hide-label',
                default: lang.eventSelection_chooseRow,
                options: getNumberedOptions(
                    selectedEvent?.maxRow,
                    lang.row,
                    lang.eventSelection_chooseRow,
                ),
                value: selectedTicket.row ? String(selectedTicket.row) : '',
                onChange: (value: string) => {
                    state.selectedTicket.row = Number(value)
                    update()
                },
                isDisabled: !selectedEvent?.maxRow,
            },
            seat: {
                class: 'select--hide-label',
                default: lang.eventSelection_chooseSeat,
                options: getNumberedOptions(
                    selectedEvent?.maxSeat,
                    lang.seat,
                    lang.eventSelection_chooseSeat,
                ),
                value: selectedTicket.seat ? String(selectedTicket.seat) : '',
                onChange: (value: string) => {
                    state.selectedTicket.seat = Number(value)
                    update()
                },
                isDisabled: !selectedEvent?.maxSeat,
            },
        }
    }

    return eventSelection
}

const getNumberedOptions = (
    count: number | undefined,
    prefix: string,
    placeholder: string,
): SelectOption[] => {
    if (count === undefined) {
        return [{ value: '', name: placeholder }]
    }

    const options: SelectOption[] = []

    for (let i = 1; i <= count; i++) {
        options.push({
            value: String(i),
            name: `${prefix} ${i}`,
        })
    }

    return options
}

const addTicketsStepSections = (checkout: CheckoutProps, view: EventView) => {
    const { state } = view
    const { lang, floorplan, labels, seats } = state

    if (state.isAddProductsFlow) {
        checkout.productsSidebar = getProductsSidebar(view)

        if (state.isProductSaleEnabled) {
            checkout.eventSelectionSection = getEventSelectionProps(view)
        } else {
            checkout.text = lang.products_disabled
        }

        return
    }

    if (floorplan) {
        checkout.floorplanSection = getFloorplanSection(view, floorplan)
    } else {
        const { title, text } = labels.intro
        const hasSeatCount = typeof seats.freeCount === 'number'

        if (title || text || hasSeatCount) {
            checkout.introSection = { title, text }

            if (hasSeatCount) {
                checkout.introSection.freeSeats = {
                    label: lang.intro_freeSeats,
                    count: String(seats.freeCount),
                }
            }
        }
    }

    if (anyTicketsAdded(state) || !state.floorplan) {
        checkout.ticketsSection = getTicketsSection(view)
    }
}

const addProductsStepSections = (checkout: CheckoutProps, view: EventView) => {
    checkout.productFiltersSection = getProductFilters(view)
    checkout.productsListSection = getProductsList(view)
    checkout.ticketsSection = getTicketsSection(view)
}

const addSummaryStepSections = (checkout: CheckoutProps, view: EventView) => {
    const { state } = view

    const { lang, isAddProductsFlow, hideDiscountCards, termsLink, products, paymentFailed, calc } =
        state

    const hasProductsInCart = products.some((product) => {
        return product.variants.some((variant) => variant.quantity > 0)
    })

    if (paymentFailed) {
        checkout.error = {
            title: lang.error_title,
            message: lang.banks_paymentFailed,
        }
    }

    if (termsLink) {
        checkout.terms = {
            label: lang.terms,
            link: view.state.termsLink,
        }
    }

    if (anyTicketsAdded(state) || (isAddProductsFlow && hasProductsInCart)) {
        checkout.ticketsSection = getTicketsSection(view)

        if (!hideDiscountCards) {
            checkout.cardsSection = getCardsSection(view)
        }

        checkout.contactSection = getContactSection(view)

        // We don't display the payment section if price is 0
        if (state.paymentOptions.length > 0 && calc.toPay !== 0) {
            checkout.paymentSection = getPaymentSection(view)
        }

        // We need to ensure that there is a default payment method always selected when paying with 0
        if (calc.toPay === 0 && state.paymentOptions.length > 0) {
            Storage.update({ selectedPaymentGateway: state.paymentOptions[0].name })
        }

        checkout.buttons.push({
            text: lang.submit_pay,
            onClick: async () => {
                try {
                    handleGtmPay(view.state.calc)
                    await EventActions.submit(view)
                } catch (error) {
                    handleError(view, error as Error)
                }
            },
            isDisabled: Boolean(state.validationErrors.gateway),
        })
    } else if (isAddProductsFlow) {
        checkout.text = lang.cartEmpty
    }
}

const getSelectedSeats = (view: EventView): SelectedSeatProps | undefined => {
    const { state, update } = view

    const { lang, session, isAddProductsFlow, isProductSaleEnabled, activeCheckoutStep, calc } =
        state

    const items: SelectedSeatItem[] = []

    if (isAddProductsFlow) {
        if (session?.seatNumber && session?.rowNumber) {
            items.push({
                text: `${lang.row} ${session.rowNumber}, ${lang.seat} ${session.seatNumber}`,
            })
        }
    } else {
        for (const group of calc.tickets) {
            for (const seat of group.seats ?? []) {
                items.push({
                    text: `${group.name}, ${seat.name}`,
                })
            }
        }
    }

    if (!items.length) {
        return
    }

    const props: SelectedSeatProps = {
        title: lang.myTickets,
        items,
        onToggle: () => {
            state.isMyTicketsSectionOpen = !state.isMyTicketsSectionOpen
            update()
        },
        isOpen: isAddProductsFlow || state.isMyTicketsSectionOpen,
        disableToggle: isAddProductsFlow,
    }

    if (isProductSaleEnabled && activeCheckoutStep !== CheckoutStep.TICKETS) {
        props.changeLink = {
            text: lang.change,
            onClick: () => {
                state.activeCheckoutStep = CheckoutStep.TICKETS
                update()
                setTimeout(() => {
                    window.scrollTo({
                        top: 0,
                        behavior: 'smooth',
                    })
                }, 100)
            },
        }
    }

    return props
}

const getProductsSidebar = (view: EventView): ProductsSidebarProps => {
    const { state } = view
    const { lang, scheduleUrl } = state

    return {
        title: lang.products_sidebar_title,
        firstParagraph: lang.products_sidebar_firstParagraph,
        textBeforeLink: lang.products_sidebar_textBeforeLink,
        link: {
            text: lang.products_sidebar_linkText,
            url: scheduleUrl,
        },
        textAfterLink: lang.products_sidebar_textAfterLink,
    }
}

export const getCheckout = (view: EventView): CheckoutProps => {
    const { state } = view

    const {
        isLoading,
        theme,
        lang,
        isAddProductsFlow,
        sessionEndTime,
        details,
        production,
        floorplan,
        labels,
        isProductSaleEnabled,
        activeCheckoutStep,
        calc,
    } = state

    const checkout: CheckoutProps = {
        production,
        theme,
        dateTime: details.datetime,
        venueName: labels.intro.venueName,
        additionalInfo: details.additionalInfo,
        selectedSeats: getSelectedSeats(view),
        isFree: !calc.toPay,
        hasFloorplan: Boolean(floorplan),
        activeCheckoutStep,
        isLoading,
        buttons: [],
        isProductSaleEnabled: !!state.isProductSaleEnabled,
        productSelectionText: labels.productSelection,
        paySectionText: labels.paySection,
    }

    if (!isAddProductsFlow) {
        checkout.timer = {
            label: lang.timer_label,
            endTime: sessionEndTime,
        }
    }

    if (isProductSaleEnabled) {
        checkout.progressBar = getProgressBarProps(view)
        addNavigationButtons(checkout, view)
    }

    if (!isProductSaleEnabled || activeCheckoutStep === CheckoutStep.TICKETS) {
        addTicketsStepSections(checkout, view)
    }

    if (isProductSaleEnabled && activeCheckoutStep === CheckoutStep.PRODUCTS) {
        addProductsStepSections(checkout, view)
    }

    if (
        (!isAddProductsFlow && !isProductSaleEnabled) ||
        activeCheckoutStep === CheckoutStep.SUMMARY
    ) {
        addSummaryStepSections(checkout, view)
    }

    return checkout
}
